<template>
  <b-container fluid>
    <div v-if="normalizedItems.length > 0" class="message-log-search-row mt-2">
      <div class="message-log-search-col">
        <div class="font-weight-bold mb-1 message-log-search-label">
          Search Name:
        </div>
        <b-form-input
          v-model.trim="search"
          placeholder="Type to search"
          class="message-log-search-input"
        />
      </div>
    </div>

    <div v-if="!loading && !items.length" class="py-4 text-center">
      <h6 class="text-secondary mb-0">{{ emptyText }}</h6>
    </div>

    <div v-else class="message-log-table-row">
      <b-table
        class="message-log-table mb-3 mt-0 d-none d-sm-table"
        responsive
        hover
        show-empty
        :fields="fields"
        :items="pagedItems"
        :busy="loading"
        fixed
        tbody-tr-class="clickable-row"
      >
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <digger-loader />
          </div>
        </template>

        <template #head()="data">
          <span class="text-capitalize">{{ data.label }}</span>
        </template>

        <template #empty>
          <h6 class="text-center mb-0">{{ emptyText }}</h6>
        </template>

        <template #cell(name)="{ item }">
          {{ item.name }}
        </template>

        <template #cell(email)="{ item }">
          {{ item.email }}
        </template>

        <template #cell(read_at)="{ item }">
          {{ formatReadAt(item.read_at) }}
        </template>

        <template #cell(status)="{ item }">
          <b-badge
            pill
            :variant="item.status === 'read' ? 'success' : 'secondary'"
          >
            {{ item.status === 'read' ? 'Read' : 'Not Read' }}
          </b-badge>
        </template>
      </b-table>

      <!-- Mobile-only: name + email stacked on the left, status on the
           right. No column headers needed since the layout is self-evident. -->
      <div class="d-sm-none">
        <div v-if="loading" class="text-center text-primary my-2">
          <digger-loader />
        </div>
        <div
          v-else-if="!pagedItems.length"
          class="text-center text-secondary my-2"
        >
          <h6 class="mb-0">{{ emptyText }}</h6>
        </div>
        <div v-else class="message-log-mobile-list mb-3">
          <div
            v-for="(item, i) in pagedItems"
            :key="item.id || i"
            class="message-log-mobile-row"
          >
            <div class="message-log-mobile-info">
              <div class="message-log-mobile-name">{{ item.name }}</div>
              <div class="message-log-mobile-email">{{ item.email }}</div>
            </div>
            <b-badge
              pill
              class="message-log-mobile-status"
              :variant="item.status === 'read' ? 'success' : 'secondary'"
            >
              {{ item.status === 'read' ? 'Read' : 'Not Read' }}
            </b-badge>
          </div>
        </div>
      </div>

      <b-pagination
        v-if="rows > perPage"
        v-model="currentPage"
        :total-rows="rows"
        :per-page="perPage"
        align="center"
        class="mt-3 mb-3"
      />
    </div>
  </b-container>
</template>

<script>
import dayjs from 'dayjs'
import { parseServerDate } from '@/utils/dateHelper'

export default {
  name: 'MessageLogRecipientsList',
  data() {
    return {
      currentPage: 1,
      perPage: 20,
      search: '',
    }
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    emptyText: {
      type: String,
      default: 'No recipients found.',
    },
  },
  watch: {
    items() {
      this.currentPage = 1
    },
    search() {
      this.currentPage = 1
    },
  },
  computed: {
    fields() {
      return [
        { key: 'name', label: 'Name', class: 'w-30' },
        { key: 'email', label: 'Email', class: 'w-35' },
        { key: 'read_at', label: 'Read At', class: 'w-20' },
        { key: 'status', label: 'Status', class: 'w-15' },
      ]
    },
    normalizedItems() {
      return (this.items || []).map((r) => ({
        id: r.id || null,
        name: r.full_name || r.name || '-',
        email: r.email || '-',
        read_at: r.read_at || r.readAt || (r.pivot && r.pivot.read_at) || null,
        status:
          r.read ||
          r.read_at ||
          r.readAt ||
          (r.pivot && (r.pivot.read || r.pivot.read_at))
            ? 'read'
            : 'unread',
      }))
    },
    filteredItems() {
      if (!this.search) return this.normalizedItems
      const q = this.search.toLowerCase()
      return this.normalizedItems.filter((item) =>
        (item.name || '').toLowerCase().includes(q),
      )
    },
    rows() {
      return this.filteredItems.length
    },
    pagedItems() {
      const start = (this.currentPage - 1) * this.perPage
      return this.filteredItems.slice(start, start + this.perPage)
    },
  },
  methods: {
    formatReadAt(value) {
      // read_at comes from a raw SQL aggregate on the API (see
      // MassNotificationController), so it arrives as a naive
      // "Y-m-d H:i:s" string with no timezone marker — parseServerDate
      // treats that as UTC instead of letting dayjs assume local time.
      const d = parseServerDate(value)
      return d ? dayjs(d).format('DD MMM YYYY h:mm A') : '-'
    },
  },
}
</script>

<style lang="scss" scoped>
/deep/ .message-log-table thead th {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

/deep/ .message-log-table tbody td {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

.message-log-search-row {
  margin: 0 0 0.35rem 0;
}

.message-log-search-col {
  width: 100%;
  max-width: 420px;
}

.message-log-table-row {
  margin-top: 0;
}

::v-deep(.clickable-row:hover) {
  cursor: pointer;
}

// Small-phone-only: the table has 4 columns (Name/Email/Read At/Status)
// which can't fit at 320px. Rather than breaking each entry into 4 stacked
// rows, keep one row per recipient and drop the least essential column
// (Read At — Status already conveys read/unread) so the rest fit. It
// reappears once there's room, at 576px and up.
@media (max-width: 575.98px) {
  .message-log-search-col {
    max-width: 100%;
  }

  .message-log-search-label {
    font-size: 0.85rem;
  }

  /deep/ .message-log-search-input {
    font-size: 0.85rem;
    height: calc(1.5em + 0.5rem + 2px);
    padding: 0.25rem 0.5rem;
  }

  // Below 576px the table (hidden via d-none d-sm-table in the template)
  // is replaced by a simple list: name + email stacked on the left,
  // status badge on the right. No column headers needed.
  .message-log-mobile-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.6rem 0;
    border-bottom: 1px solid #e9ecef;

    &:last-child {
      border-bottom: none;
    }
  }

  .message-log-mobile-info {
    min-width: 0;
    flex: 1 1 auto;
  }

  .message-log-mobile-name {
    font-weight: 700;
    font-size: 0.9rem;
    word-break: break-word;
  }

  .message-log-mobile-email {
    font-size: 0.8rem;
    color: #6c757d;
    word-break: break-word;
  }

  .message-log-mobile-status {
    flex: 0 0 auto;
    font-size: 0.75rem;
    margin-top: 0.15rem;
  }
}
</style>
